/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import TimelineChart from './ChartWrappers/TimelineChart';
import AntennaPointingAnglesChart from './ChartWrappers/AntennaPointingAnglesChart';
import CumulativeDataUsageChart from './ChartWrappers/CumulativeDataUsageChart';
import FlDataRateChart from './ChartWrappers/FlDataRateChart';
import SpeedAndAltitudeChart from './ChartWrappers/SpeedAndAltitudeChart';
import RlDataRateChart from './ChartWrappers/RlDataRateChart';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import PingSuccessChart from './ChartWrappers/PingSuccessChart';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import PingLatencyChart from './ChartWrappers/PingLatencyChart';
import RlProtocolUsageChart from './ChartWrappers/RlProtocolUsageChart';
import FlProtocolUsageChart from './ChartWrappers/FlProtocolUsageChart';
import { NetworkGroup, TerminalTypeLabel } from '../../../../shared/entities/terminal';
import { TerminalPlatformsEnum } from '../../../../shared/entities/terminalPlatform';
import TotalDataVolumeChart from './ChartWrappers/TotalDataVolumeChart';
import SnrChart from './ChartWrappers/SnrChart';
import PowerChart from './ChartWrappers/PowerChart';

/**
 * Enum with unique keys for the chart types.
 */
export enum ChartItemKeys {
  timeline = 'timeline',
  speedAndAltitude = 'speedAndAltitude',
  flDataRate = 'flDataRate',
  rlDataRate = 'rlDataRate',
  packetLossPercentage = 'packetLossPercentage',
  pingAvgLatency = 'pingAvgLatency',
  fLProtocolUsage = 'fLProtocolUsage',
  fLProtocolUsageSecondaryTerminalHistory = 'fLProtocolUsageSecondaryTerminalHistory',
  rLProtocolUsage = 'rlProtocolUsage',
  rLProtocolUsageSecondaryTerminalHistory = 'rLProtocolUsageSecondaryTerminalHistory',
  cumDataUsage = 'cumDataUsage',
  antennaPointingAngles = 'antennaPointingAngles',
  antennaPointingAnglesSecondaryTerminalHistory = 'antennaPointingAnglesSecondaryTerminalHistory',
  totalDataVolume = 'totalDataVolume',
  snr = 'snr',
  power = 'power',
}

export default interface ChartMetadata {
  text: string;
  component: React.ComponentType<any>;
  onlyUsePrimaryTerminalHistory?: boolean;
  onlyUseSecondaryTerminalHistory?: boolean;
  showKuOrKaChartNamePrefix?: boolean;
  isRestricted?: boolean;
  acceptedTerminalTypes?: TerminalTypeLabel[];
  acceptedPlatformTypes?: TerminalPlatformsEnum[];
  acceptedNetworkTypes: NetworkGroup[];
  alwaysShow?: boolean;
  useSelectedKuPlusKaTabTerminalHistory?: boolean;
}

export interface ChartMetadataList {
  [key: string]: ChartMetadata;
}

/**
 * Map of chart keys to chart metadata. Chart metadata includes the chart title and chart component.
 * TODO: uncomment the other charts once the data is available
 */
export const chartContent: ChartMetadataList = {
  [ChartItemKeys.timeline]: {
    text: 'Timeline',
    component: TimelineChart,
    isRestricted: true,
    alwaysShow: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.totalDataVolume]: {
    text: 'Total Data Volume',
    component: TotalDataVolumeChart,
    acceptedNetworkTypes: [NetworkGroup.GX],
  },
  [ChartItemKeys.snr]: {
    text: 'SNR',
    component: SnrChart,
    acceptedNetworkTypes: [NetworkGroup.GX],
  },
  [ChartItemKeys.power]: {
    text: 'Power',
    component: PowerChart,
    acceptedNetworkTypes: [NetworkGroup.GX],
  },
  [ChartItemKeys.flDataRate]: {
    text: 'FL Data Rate',
    component: FlDataRateChart,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.rlDataRate]: {
    text: 'RL Data Rate',
    component: RlDataRateChart,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.packetLossPercentage]: {
    text: 'Viasat Network Ping',
    component: PingSuccessChart,
    acceptedTerminalTypes: [
      TerminalTypeLabel.KA,
      TerminalTypeLabel.KUKA,
      TerminalTypeLabel.CWS1,
      TerminalTypeLabel.CWS2,
      TerminalTypeLabel.MMT,
      TerminalTypeLabel.VI,
    ],
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  // [ChartItemKeys.pingAvgLatency]: { text: 'ftv.charts.pingAvgLatency', component: PingLatencyChart, acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT], },
  [ChartItemKeys.fLProtocolUsage]: {
    text: 'FL Traffic Composition',
    component: FlProtocolUsageChart,
    onlyUsePrimaryTerminalHistory: true,
    showKuOrKaChartNamePrefix: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.rLProtocolUsage]: {
    text: 'RL Traffic Composition',
    component: RlProtocolUsageChart,
    onlyUsePrimaryTerminalHistory: true,
    showKuOrKaChartNamePrefix: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.fLProtocolUsageSecondaryTerminalHistory]: {
    text: 'FL Protocol Usage',
    component: FlProtocolUsageChart,
    onlyUseSecondaryTerminalHistory: true,
    showKuOrKaChartNamePrefix: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.rLProtocolUsageSecondaryTerminalHistory]: {
    text: 'RL Protocol Usage',
    component: RlProtocolUsageChart,
    onlyUseSecondaryTerminalHistory: true,
    showKuOrKaChartNamePrefix: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.cumDataUsage]: {
    text: 'Cumulative Data Usage',
    component: CumulativeDataUsageChart,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.speedAndAltitude]: {
    text: 'Speed and Altitude',
    component: SpeedAndAltitudeChart,
    isRestricted: true,
    acceptedPlatformTypes: [TerminalPlatformsEnum.Airborne, TerminalPlatformsEnum.SpaceVehicle],
    alwaysShow: true,
    // On dual-modem (Ku+Ka) flights, show primary (Ka) history on the Overview and Ka Ant. tabs, secondary (Ku) history on the Ku Ant. tab
    useSelectedKuPlusKaTabTerminalHistory: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.antennaPointingAngles]: {
    text: 'Antenna Pointing Angles',
    component: AntennaPointingAnglesChart,
    onlyUsePrimaryTerminalHistory: true,
    showKuOrKaChartNamePrefix: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
  [ChartItemKeys.antennaPointingAnglesSecondaryTerminalHistory]: {
    text: 'Antenna Pointing Angles',
    component: AntennaPointingAnglesChart,
    onlyUseSecondaryTerminalHistory: true,
    showKuOrKaChartNamePrefix: true,
    acceptedNetworkTypes: [NetworkGroup.VIASAT_ARCLIGHT],
  },
};
